import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCSelect, SCSelectV2, SCSwitch } from 'rollup-allspark';
import HelpPopover from '../../../components/HelpPopover';
import { DEFAULT_ALL_PAGE_SIZE, DEFAULT_PAGE_NO } from '../../../config';
import {
  ANALYTICS_MODULES,
  COMPARISON_TYPE,
  OWN_CHANNELS,
  TIME_PERIOD_LIST,
  TOOLTIP_LABELS,
} from '../../../config/constants/analytics-v2';
import {
  TIME_PERIOD_HELP,
  TIME_RANGE_HELP,
} from '../../../config/HelpPopoverTexts/nativeAnalyticsPopoverTexts';
import { isEmpty } from '../../../utils';
import { makeSelectGlobalProperty } from '../../App/selectors';
import { loadList, updateEntity } from '../actions';
import { BRANCH_LIST, BRAND_LIST, PROVIDER_LIST } from '../sagaMap';
import { makeSelectProperty } from '../selectors';
import TimeSelector from './TimeSelector';

const EntitySelector = ({
  branches = [],
  brands = [],
  businesses = [],
  cities = [],
  getBranches,
  getBrands,
  getProviders,
  providers = [],
  selectedBranch,
  selectedBrand,
  selectedBusiness,
  selectedCities,
  selectedComparisonType,
  selectedOwnChannel,
  selectedProvider,
  selectedTimePeriod,
  selectedTimeRange,
  selectedTab,
  updateField = () => {},
}) => {
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [isProviderComparisonPopoverOpen, setIsProviderComparisonPopoverOpen] =
    useState(false);
  const [isTimeSelectorOpen, setIsTimeSelectorOpen] = useState(false);
  const [isTimePeriodPopoverOpen, setIsTimePeriodPopoverOpen] = useState(false);
  const [isTimeRangePopoverOpen, setIsTimeRangePopoverOpen] = useState(false);

  const prevTabRef = useRef(selectedTab);
  const nonCashFilteredBranchesRef = useRef([]);

  useEffect(() => {
    const wasCash = prevTabRef.current === ANALYTICS_MODULES.CASH;
    const isCash = selectedTab === ANALYTICS_MODULES.CASH;

    if (!wasCash && isCash) {
      nonCashFilteredBranchesRef.current = filteredBranches;
      setFilteredBranches([]);
    } else if (wasCash && !isCash) {
      const cityIds = (selectedCities || []).map(c => c.id);
      const restoredFilteredBranches = !isEmpty(
        nonCashFilteredBranchesRef.current,
      )
        ? nonCashFilteredBranchesRef.current
        : branches.filter(b => cityIds.includes(b.city_id));

      setFilteredBranches(restoredFilteredBranches);
      nonCashFilteredBranchesRef.current = [];
    }

    prevTabRef.current = selectedTab;
  }, [selectedTab]);

  // Load branches when Cash tab is selected and business is selected
  useEffect(() => {
    if (
      selectedTab === ANALYTICS_MODULES.CASH &&
      !isEmpty(selectedBusiness)
    ) {
      getBranches({
        ...BRANCH_LIST,
        params: {
          page: DEFAULT_PAGE_NO,
          size: DEFAULT_ALL_PAGE_SIZE,
          filterByBusiness: selectedBusiness.id,
        },
        callbackFn: ll => {
          setFilteredBranches(ll);
        },
      });
    }
  }, [selectedTab, selectedBusiness]);

  const handleBusinessChange = biz => {
    if (!isEmpty(biz)) {
      getBranches({
        ...BRANCH_LIST,
        params: {
          page: DEFAULT_PAGE_NO,
          size: DEFAULT_ALL_PAGE_SIZE,
          filterByBusiness: biz.id,
        },
        callbackFn: ll => {
          // For Cash tab, only set filtered branches (don't auto-select)
          if (selectedTab === ANALYTICS_MODULES.CASH) {
            setFilteredBranches(ll);
            // Don't auto-select branches, let user choose
            return;
          }

          const fCities = [];
          // const branchMap = {};
          // const providerMap = {};

          if (!isEmpty(ll)) {
            const countryIds = new Set();
            const cityIds = new Set();

            ll.forEach(l => {
              countryIds.add(l.country_id);
              cityIds.add(l.city_id);
              // branchMap[l.id] = l.title;
            });

            getProviders({
              ...PROVIDER_LIST,
              params: {
                page: DEFAULT_PAGE_NO,
                size: DEFAULT_ALL_PAGE_SIZE,
                filterByCountry: [...countryIds].join(','),
              },
              callbackFn: al => {
                // if (!isEmpty(al)) {
                //   al.forEach(p => {
                //     providerMap[p.id] = p.title;
                //   });
                // }
                updateField({
                  selectedOwnChannel: [...OWN_CHANNELS],
                  selectedProvider: al,
                });
              },
            });

            cities.forEach(c => {
              if (cityIds.has(c.id)) {
                fCities.push(c);
              }
            });
          }

          setFilteredBranches(ll);
          setFilteredCities(fCities);

          // On initial load, select all cities and all branches
          updateField({
            // branchMap,
            // providerMap,
            selectedBranch: ll,
            selectedCities: fCities,
          });
        },
      });

      getBrands({
        ...BRAND_LIST,
        params: {
          page: DEFAULT_PAGE_NO,
          size: DEFAULT_ALL_PAGE_SIZE,
          filterByBusiness: biz.id,
        },
        callbackFn: bl => {
          // const brandMap = bl.reduce((acc, brand) => {
          //   acc[brand.id] = brand.title;
          //   return acc;
          // }, {});

          updateField({
            // brandMap,
            selectedBrand: bl,
          });
        },
      });
    }

    updateField({
      selectedBusiness: biz,
    });
  };

  const handleBranchChange = branch => {
    updateField({
      selectedBranch: branch,
    });
  };

  const handleBrandChange = brand => {
    updateField({
      selectedBrand: brand,
    });
  };

  const handleCityChange = c => {
    const cityIds = c.map(fc => fc.id);
    const fb = branches.filter(b => cityIds.includes(b.city_id));

    // Get previous city IDs to detect newly selected and unselected cities
    const previousCityIds = selectedCities ? selectedCities.map(sc => sc.id) : [];
    const newlySelectedCityIds = cityIds.filter(
      cityId => !previousCityIds.includes(cityId)
    );
    const unselectedCityIds = previousCityIds.filter(
      cityId => !cityIds.includes(cityId)
    );

    // Start with current selected branches
    let updatedSelectedBranches = selectedBranch ? [...selectedBranch] : [];

    // 1. When a city is unselected, remove all branches from that city
    if (unselectedCityIds.length > 0) {
      updatedSelectedBranches = updatedSelectedBranches.filter(
        b => !unselectedCityIds.includes(b.city_id)
      );
    }

    // 2. When a city is selected, add all branches from that city
    if (newlySelectedCityIds.length > 0) {
      const branchesToAdd = fb.filter(
        b => newlySelectedCityIds.includes(b.city_id)
      );

      // Merge new branches with existing selection (avoid duplicates)
      const existingBranchIds = new Set(
        updatedSelectedBranches.map(b => b.id)
      );
      const newBranches = branchesToAdd.filter(
        b => !existingBranchIds.has(b.id)
      );
      updatedSelectedBranches = [...updatedSelectedBranches, ...newBranches];
    }

    setFilteredBranches(fb);
    updateField({
      selectedCities: c,
      selectedBranch: updatedSelectedBranches,
    });
  };

  const handleProviderChange = provider => {
    updateField({ selectedProvider: provider });
  };

  const handleOwnChannelChange = channel => {
    updateField({ selectedOwnChannel: channel });
  };

  const handleTimePeriodChange = period => {
    updateField({ selectedTimePeriod: period });
  };

  const onTimeSelected = timeRange => {
    updateField({ selectedTimeRange: timeRange });
    setIsTimeSelectorOpen(false);
  };

  const onComparatorChange = type => v => {
    updateField({ selectedComparisonType: v ? type : null });
  };

  // Universal channel-breakdown control (replaces the 3 Compare-by toggles).
  const breakdownOn = !!selectedComparisonType;
  const currentComparisonDimension =
    selectedComparisonType || COMPARISON_TYPE.AGGREGATOR;
  const COMPARISON_DIMENSIONS = [
    { type: COMPARISON_TYPE.AGGREGATOR, label: 'Aggregator' },
    { type: COMPARISON_TYPE.BRAND, label: 'Brand' },
    { type: COMPARISON_TYPE.BRANCH, label: 'Branch' },
  ];

  const timeSelectorLabelEl = (() => {
    if (isEmpty(selectedTimeRange)) {
      return 'Select Start and End Time';
    }

    return (
      <div className="flex w-full justify-between items-center">
        <div>
          {selectedTimeRange?.startTime?.title} -{' '}
          {selectedTimeRange?.endTime?.title}
        </div>
        <i
          role="presentation"
          className="kt-x text-black cursor-pointer text-xl"
          onClick={() => updateField({ selectedTimeRange: null })}
        />
      </div>
    );
  })();

  const timeSelectorEl = isTimeSelectorOpen ? (
    <TimeSelector
      timeRange={selectedTimeRange}
      onTimeSelected={onTimeSelected}
    />
  ) : null;

  const timePeriodTitleEl = (
    <div className="flex gap-x-2 text-grey-darker">
      <div className="text-xs">Time Period</div>
      <HelpPopover
        isPopoverOpen={isTimePeriodPopoverOpen}
        position="right"
        isHtmlElement
        content={TIME_PERIOD_HELP}
        setIsPopoverOpen={setIsTimePeriodPopoverOpen}
        iconClassNames="sc-information cursor-pointer"
      />
    </div>
  );

  // For Cash tab, only show Business and Branch
  if (selectedTab === ANALYTICS_MODULES.CASH) {
    return (
      <div className="flex flex-col gap-y-4">
        <div className="grid grid-cols-2 gap-4">
          <SCSelect
            searchable
            options={businesses}
            trackBy="id"
            customLabel={b => `${b.id}. ${b.title}`}
            searchAbleAttribute={['title', 'id']}
            title="Business"
            placeholder="Select Business"
            value={selectedBusiness}
            onSelect={handleBusinessChange}
          />
          <SCSelectV2
            searchable
            multiple
            options={filteredBranches}
            title="Branch"
            trackBy="id"
            customLabel={b => `${b.id}. ${b.title}`}
            searchAbleAttribute={['title', 'id']}
            placeholder="Select Branches"
            value={selectedBranch}
            onSelect={handleBranchChange}
            disabled={isEmpty(selectedBusiness) || isEmpty(branches)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div className="grid grid-cols-4 gap-4">
        <SCSelect
          searchable
          options={businesses}
          trackBy="id"
          customLabel={b => `${b.id}. ${b.title}`}
          searchAbleAttribute={['title', 'id']}
          title="Business"
          placeholder="Select Business"
          value={selectedBusiness}
          onSelect={handleBusinessChange}
        />
        <SCSelectV2
          searchable
          multiple
          options={filteredCities}
          trackBy="id"
          label="title"
          title="City"
          placeholder="Select city"
          value={selectedCities}
          onSelect={handleCityChange}
          disabled={isEmpty(selectedBusiness) || isEmpty(brands)}
        />
        <SCSelectV2
          searchable
          multiple
          options={filteredBranches}
          title="Branch"
          trackBy="id"
          customLabel={b => `${b.id}. ${b.title}`}
          searchAbleAttribute={['title', 'id']}
          placeholder="Select Branch"
          value={selectedBranch}
          onSelect={handleBranchChange}
          disabled={
            isEmpty(selectedBusiness) ||
            isEmpty(selectedCities) ||
            isEmpty(branches)
          }
        />
        <SCSelectV2
          multiple
          searchable
          options={brands}
          title="Brand"
          trackBy="id"
          customLabel={b => `${b.id}. ${b.title}`}
          searchAbleAttribute={['title', 'id']}
          placeholder="Select Brand"
          value={selectedBrand}
          onSelect={handleBrandChange}
          disabled={isEmpty(selectedBusiness) || isEmpty(selectedCities)}
        />
        <SCSelectV2
          multiple
          searchable
          options={providers}
          title="Provider"
          trackBy="id"
          customLabel={b => `${b.id}. ${b.title}`}
          searchAbleAttribute={['title', 'id']}
          placeholder="Select Provider"
          value={selectedProvider}
          onSelect={handleProviderChange}
          disabled={
            isEmpty(selectedBusiness) ||
            isEmpty(selectedCities) ||
            isEmpty(branches)
          }
        />
        <SCSelect
          multiple
          options={OWN_CHANNELS}
          title="Own Channel"
          trackBy="id"
          label="title"
          searchAbleAttribute={['title', 'id']}
          placeholder="Select Own Channel"
          value={selectedOwnChannel}
          onSelect={handleOwnChannelChange}
          disabled={
            isEmpty(selectedBusiness) ||
            isEmpty(selectedCities) ||
            isEmpty(selectedProvider)
          }
        />
        <div className="flex flex-col gap-y-1 relative">
          <div className="flex gap-x-2 text-grey-darker">
            <div className="text-xs">Time Range</div>
            <HelpPopover
              isPopoverOpen={isTimeRangePopoverOpen}
              position="right"
              isHtmlElement
              content={TIME_RANGE_HELP}
              setIsPopoverOpen={setIsTimeRangePopoverOpen}
              iconClassNames="sc-information cursor-pointer leading-4"
            />
          </div>
          <div
            role="presentation"
            className="flex items-center rounded-lg border border-grey h-9 px-4 cursor-pointer"
            onClick={() => setIsTimeSelectorOpen(!isTimeSelectorOpen)}
          >
            {timeSelectorLabelEl}
          </div>
          {timeSelectorEl}
        </div>
        <div className="flex flex-col gap-y-1">
          <div className="flex gap-x-2 text-grey-darker">
            <div className="text-xs">Time Period</div>
            <HelpPopover
              isPopoverOpen={isTimePeriodPopoverOpen}
              position="right"
              isHtmlElement
              content={TIME_PERIOD_HELP}
              setIsPopoverOpen={setIsTimePeriodPopoverOpen}
              iconClassNames="sc-information cursor-pointer leading-4"
            />
          </div>
          <SCSelect
            options={TIME_PERIOD_LIST}
            title=""
            trackBy="id"
            label="title"
            placeholder="Select Time Period"
            value={selectedTimePeriod}
            onSelect={handleTimePeriodChange}
          />
        </div>
      </div>
      {selectedTab === ANALYTICS_MODULES.SALES && (
      <div className="flex justify-between">
        {/* Channel breakdown — one switch + dimension picker (was 3 toggles) */}
        <div className="flex flex-wrap items-center gap-x-3">
          <div className="flex h-10 items-center gap-x-2">
            <span className="text-base font-medium text-black">
              Channel breakdown
            </span>
            <HelpPopover
              isPopoverOpen={isProviderComparisonPopoverOpen}
              position="left"
              isHtmlElement
              content={TOOLTIP_LABELS.AGGREGATOR_COMPARISON_HELP}
              setIsPopoverOpen={setIsProviderComparisonPopoverOpen}
              iconClassNames="sc-information cursor-pointer flex items-center"
            />
            <SCSwitch
              value={breakdownOn}
              handleChange={value =>
                updateField({
                  selectedComparisonType: value
                    ? currentComparisonDimension
                    : null,
                })
              }
            />
          </div>
          {breakdownOn && (
            <div className="flex items-center gap-x-1 rounded-md bg-grey-light p-1">
              {COMPARISON_DIMENSIONS.map(dim => {
                const isActive = selectedComparisonType === dim.type;
                return (
                  <button
                    key={dim.type}
                    type="button"
                    onClick={() => onComparatorChange(dim.type)(true)}
                    className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-grey-darker hover:text-black'
                    }`}
                  >
                    {dim.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

EntitySelector.propTypes = {
  branches: PropTypes.array,
  brands: PropTypes.array,
  businesses: PropTypes.array,
  cities: PropTypes.array,
  getBranches: PropTypes.func,
  getBrands: PropTypes.func,
  getProviders: PropTypes.func,
  providers: PropTypes.array,
  selectedBranch: PropTypes.array,
  selectedBrand: PropTypes.array,
  selectedBusiness: PropTypes.object,
  selectedCities: PropTypes.array,
  selectedComparisonType: PropTypes.string,
  selectedOwnChannel: PropTypes.array,
  selectedProvider: PropTypes.array,
  selectedTimePeriod: PropTypes.object,
  selectedTimeRange: PropTypes.object,
  selectedTab: PropTypes.string,
  updateField: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  branches: makeSelectProperty('branches'),
  brands: makeSelectProperty('brands'),
  businesses: makeSelectGlobalProperty('businessList'),
  cities: makeSelectProperty('cities'),
  providers: makeSelectProperty('analyticsProviders'),
  selectedBranch: makeSelectProperty('selectedBranch'),
  selectedBrand: makeSelectProperty('selectedBrand'),
  selectedBusiness: makeSelectProperty('selectedBusiness'),
  selectedCities: makeSelectProperty('selectedCities'),
  selectedComparisonType: makeSelectProperty('selectedComparisonType'),
  selectedOwnChannel: makeSelectProperty('selectedOwnChannel'),
  selectedProvider: makeSelectProperty('selectedProvider'),
  selectedTimePeriod: makeSelectProperty('selectedTimePeriod'),
  selectedTimeRange: makeSelectProperty('selectedTimeRange'),
  selectedTab: makeSelectProperty('selectedTab'),
});

const mapDispatchToProps = dispatch => ({
  getBranches: meta => dispatch(loadList(meta)),
  getBrands: meta => dispatch(loadList(meta)),
  getProviders: meta => dispatch(loadList(meta)),
  updateField: value => dispatch(updateEntity(value)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(EntitySelector);
